import { useState } from "react"
import { FaCircleCheck } from "react-icons/fa6"
import { Box, Center, HStack, RadioGroup, Separator, Spacer, Stack, VStack } from "@chakra-ui/react"
import { Button, Text, TextField } from "@/components"
import { useAuth } from "@/contexts"
import { useInsertBc } from "@/hooks"
import { Deal, DealStep } from "@/types"

interface Props {
  currentDeal: Deal
}

export const BackgroundCheckFormComponent = ({ currentDeal }: Props) => {
  const yesNoItems = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ]

  const { user } = useAuth()

  const [legalName, setLegalName] = useState<string>("")
  const [preferredEmail, setPreferredEmail] = useState<string>("")
  const [address, setAddress] = useState<string>("")
  const [city, setCity] = useState<string>("")
  const [state, setState] = useState<string>("")
  const [zipCode, setZipCode] = useState<string>("")
  const [phoneNumber, setPhoneNumber] = useState<string>("")
  const [ssn, setSsn] = useState<string>("")
  const [birthdate, setBirthdate] = useState<string>("")
  const [isUsCitizen, setIsUsCitizen] = useState<string>("")
  const [isMarried, setIsMarried] = useState<string>("")
  const [spouseName, setSpouseName] = useState<string>("")
  const [spouseEmail, setSpouseEmail] = useState<string>("")
  
  const { mutateAsync: insertBcInfo, isPending, isSuccess } = useInsertBc()

  if (!user) return null

  const handleSubmit = async () => {
    if (user.userType !== "client") return null
    try {
      if (!legalName || !preferredEmail || !address || !city || !state || !zipCode || !phoneNumber || !ssn || !birthdate || !isUsCitizen || !isMarried) {
        alert("Please fill out all required fields.")
        return
      }

      const bcData = {
        dealId: currentDeal.id,
        legalName,
        email: preferredEmail,
        address,
        city,
        state,
        zip: zipCode,
        phone: phoneNumber,
        ssn,
        birthDate: birthdate,
        isUsCitizen,
        isMarried,
        spouseName,
        spouseEmail,
      }

      await insertBcInfo(bcData)
    } catch (error) {
      console.error("Validation error:", error)
      return
    }
  }

  if (isSuccess ||
    currentDeal.portalStage === DealStep.SIGN_BC ||
    currentDeal.portalStage === DealStep.SCHEDULE_CLOSING_MEETING ||
    currentDeal.portalStage === DealStep.SIGN_CONTRACT ||
    currentDeal.portalStage === DealStep.SEND_WIRE_INFO
  ) {
    return (
      <Center w="100%" h="100%">
        <VStack position="relative" zIndex={1} w="40%" p={5} borderRadius={10} bg="card.bg" boxShadow="md" mt={20} >
          <Box borderRadius={5} bg="green.300" p={3}>
            <FaCircleCheck size={30} color="white" />
          </Box>
          <Text fontSize="lg" fontWeight="bold" textAlign="center" mt={2} mb={2}>
            Thank You!
          </Text>
          <Text fontSize="sm" textAlign="center" mb={5}>
            Thank you for signing our Background Check Form. Please check your email for a DocuSign link sent from docusign@esofund.com which you and your Equity Partner will sign.
          </Text>
          <Text fontSize="sm" textAlign="center">If you didn't receive an email please contact your Equity Partner.</Text>
        </VStack>
      </Center>
    )
  }

  return (
    <Stack w="100%" h="100%" bg="card.bg" p={10} borderLeftRadius={20} overflowY="scroll">
      <Text fontSize="md" mb={5}>
        Once submitted, you will automatically receive our e-signable credit and background check permission form via DocuSign for electronic signature.
      </Text>
      <Text>
        Note that this form cannot be accepted unless it has your full legal name (including middle names), SSN, your current home address (no PO box), and birthdate.
      </Text>
      <Text>
        Also, be sure to lift any security freeze on your credit file (all three bureaus) for at least a day so we can run the report.
      </Text>
      <Text>
        After the credit check, you might receive unsolicited financing offers from third parties. Please note that this is due to credit bureau marketing efforts, and not because ESO has shared your information. To avoid this, opt out of the credit bureau marketing efforts by visiting OptOutPreScreen.com or calling 1-888-OPT-OUT (1-888-567-8688).
      </Text>
      <Separator mt={5} mb={5} />
      <Stack gap={10}>
        <HStack gap={4} mt={4}>
          <TextField
            label="Legal Name"
            placeholder="Enter your legal name"
            type="text"
            value={legalName}
            onChange={(e) => setLegalName(e)}
          />
          <TextField
            label="Email"
            placeholder="Enter your preferred email"
            value={preferredEmail}
            onChange={(e) => setPreferredEmail(e)}
          />
        </HStack>

        <TextField
          label="Address"
          placeholder="Enter your address"
          type="text"
          value={address}
          onChange={(e) => setAddress(e)}
        />

        <TextField
          label="City"
          placeholder="Enter your city"
          type="text"
          value={city}
          onChange={(e) => setCity(e)}
        />

        <HStack gap={4}>
          <TextField
            label="State"
            placeholder="Enter your state"
            type="text"
            value={state}
            onChange={(e) => setState(e)}
          />
          <TextField
            label="Zip Code"
            placeholder="Enter your zip code"
            type="text"
            value={zipCode}
            onChange={(e) => setZipCode(e)}
          />
        </HStack>

        <TextField
          label="Phone Number"
          placeholder="Enter your phone number"
          type="phone"
          value={phoneNumber}
          required
          onChange={(e) => setPhoneNumber(e)}
        />

        <HStack gap={4}>
          <TextField
            label="Social Security Number (SSN)"
            placeholder="Enter your SSN"
            type="ssn"
            value={ssn}
            onChange={(e) => setSsn(e)}
          />

          <TextField
            label="Birthdate"
            placeholder="Enter your birthdate"
            type="date"
            value={birthdate}
            onChange={(e) => setBirthdate(e)}
          />
        </HStack>
      </Stack>
      <Separator mt={5} mb={5} />
      <HStack gap={20}>
        <Stack>
          <Text fontSize="sm" fontWeight="bold">
            Are you a U.S. Citizen?
          </Text>
          <RadioGroup.Root defaultValue="1">
            <HStack gap="6">
              {yesNoItems.map((item) => (
                <RadioGroup.Item key={item.value} value={item.value} onClick={() => setIsUsCitizen(item.value)}>
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>{item.label}</RadioGroup.ItemText>
                </RadioGroup.Item>
              ))}
            </HStack>
          </RadioGroup.Root>
        </Stack>
        
        <Stack>
          <Text fontSize="sm" fontWeight="bold">
            Are you married?
          </Text>
          <RadioGroup.Root defaultValue="1">
            <HStack gap="6">
              {yesNoItems.map((item) => (
                <RadioGroup.Item key={item.value} value={item.value} onClick={() => setIsMarried(item.value)}>
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>{item.label}</RadioGroup.ItemText>
                </RadioGroup.Item>
              ))}
            </HStack>
          </RadioGroup.Root>
        </Stack>
      </HStack>

      {
        isMarried === "yes" && (
          <HStack mt={5} gap={4}>
            <TextField
              label="Spouse Name"
              placeholder="Enter your spouse's name"
              type="text"
              value={spouseName}
              onChange={(e) => setSpouseName(e)}
            />

            <TextField
              label="Spouse Email"
              placeholder="Enter your spouse's email"
              type="text"
              value={spouseEmail}
              onChange={(e) => setSpouseEmail(e)}
            />
          </HStack>
        )
      }

      <HStack>
        <Spacer />
        <Button
          variant="solid"
          w="fit-content"
          mt={5}
          isLoading={isPending}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </HStack>
    </Stack>
  )
}
